import { Input } from '@/components/ui/input'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import useApi from '../../api';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import useAnalytics from '../../analytics/analytics';
import WebsitesList from './WebsitesList';
import { useUser } from '../../user/UserContext';
import { Button } from '@/components/ui/button';
import { SquareMousePointer, WandSparkles, Trash2, RefreshCw, ChevronRight, ChevronDown, File, Clock, Settings, Search } from 'lucide-react';
import { formatRelativeTime } from '../../utils/dateFormatter';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import PageModal from '../../websites/Page';
import FailedPagesDialog from '../../websites/FailedPagesDialog';

function WebsiteSkeleton() {
    return (
        <div className="flex flex-col w-full p-3 rounded-md border shadow-sm">
            <div className="w-full animate-pulse">
                <div className="flex flex-row justify-between w-full items-center">
                    <div className="flex items-center overflow-hidden flex-1">
                        <div className="h-5 w-5 bg-muted rounded-full mr-3 flex-shrink-0"></div>
                        <div className="flex items-center overflow-hidden flex-1">
                            <div className="h-4 w-48 bg-muted rounded"></div>
                            <div className="ml-auto pl-2 h-3 w-24 bg-muted rounded"></div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                        <div className="h-6 w-16 bg-muted rounded-md"></div>
                        <div className="h-7 w-7 bg-muted rounded-md"></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Website({
    agentId,
    indexedPages,
    setIndexedPages,
    selectedPages,
    setSelectedPages,
    website,
    setWebsite,
    agentName
}: {
    agentId: string | undefined,
    indexedPages: any,
    setIndexedPages: any,
    selectedPages: any,
    setSelectedPages: any,
    website: string,
    setWebsite: (website: string) => void,
    agentName: string
}) {
    let [isOpen, setIsOpen] = useState(false)
    const [pages, setPages] = useState(indexedPages ? indexedPages.map((page: any) => page.url) : []);
    const { get, post, remove } = useApi();
    const [loading, setLoading] = useState(true);
    const [websites, setWebsites] = useState<any[]>([]);
    const [scraping, setScraping] = useState<boolean>(false);
    const [showWebsites, setShowWebsites] = useState<boolean>(false);
    const [websiteSelected, setWebsiteSelected] = useState<any>();
    const { captureEvent } = useAnalytics();
    const { checkLicenseType } = useUser();
    const navigate = useNavigate();
    const [gettingPages, setGettingPages] = useState<boolean>(false);
    const [crawling, setCrawling] = useState<boolean>(false);
    const [retryingWebsite, setRetryingWebsite] = useState<string | null>(null);
    const [expandedWebsites, setExpandedWebsites] = useState<Set<string>>(new Set());
    const [websitePages, setWebsitePages] = useState<Record<string, any[]>>({});
    const [loadingPages, setLoadingPages] = useState<Set<string>>(new Set());
    const [websitePageInfo, setWebsitePageInfo] = useState<Record<string, { currentPage: number, totalPages: number }>>({});
    const [reindexingWebsites, setReindexingWebsites] = useState<Set<string>>(new Set());
    const [failedPagesDialog, setFailedPagesDialog] = useState<{ open: boolean, websiteId: string, pages: any[] }>({
        open: false,
        websiteId: '',
        pages: []
    });
    const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
    const [pageSearchTerm, setPageSearchTerm] = useState<string>('');

    const handleCheckboxChange = (url: string) => {
        setSelectedPages((prevSelected: any) => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(url)) {
                newSelected.delete(url);
            } else {
                newSelected.add(url);
            }
            return newSelected;
        });
    };

    const scrapeWebsite = () => {
        setGettingPages(true);
        setPages([]);
        setSelectedPages(new Set());
        try {
            const url = new URL(website);
            post('/get-website-urls/', { url: website }).then((res) => {
                setIsOpen(true);
                const sortedPages = res.data.urls.sort((a: string, b: string) => a.localeCompare(b));
                setPages(sortedPages);
                setWebsite(res.data.base_url);
                setGettingPages(false);
                toast.success('Pages retrieved successfully', toastConfig);
                captureEvent('get_website_urls', {
                    website: website,
                    urls: res.data.urls,
                });
            }).catch((error) => {
                console.error('Error scraping website:', error);
                toast.error('Error getting pages.', toastConfig);
                setGettingPages(false);
            });
        } catch (e) {
            toast.error('Invalid website URL', toastConfig);
            setGettingPages(false);
        }
    };

    const crawlWebsite = () => {
        if (!checkLicenseType('standard')) {
            toast.error('Upgrade to the Standard plan to crawl your website.', toastConfig);
            return;
        }
        setCrawling(true);
        setPages([]);
        try {
            const url = new URL(website);
            post('/crawl-website/', { website: website, agent_id: agentId }).then((res) => {
                toast.success('Crawling in progress', toastConfig);
                setCrawling(false);
                setWebsite('');
                captureEvent('crawl_website', {
                    website: website,
                    agent_id: agentId,
                });
                return res.data;
            }).catch((error) => {
                console.error('Error crawling website:', error);
                toast.error('Error getting pages.', toastConfig);
                setCrawling(false);
            });
        } catch (e) {
            toast.error('Invalid website URL', toastConfig);
            setCrawling(false);
        }
    };

    const indexPages = () => {
        if (selectedPages.size === 0) {
            toast.error('No pages selected for indexing', toastConfig);
            return;
        }
        setScraping(true);
        const selectedUrls = Array.from(selectedPages);
        post('/index-pages/', { website, urls: selectedUrls, agent_id: agentId }).then((res) => {
            setIndexedPages(res.data.pages);
            setScraping(false);
            setIsOpen(false);
            setWebsites((prevWebsites: any) => [
                ...prevWebsites,
                {
                    id: res.data.id,
                    url: website,
                    index_status: 'In Progress',
                    page_count: 0,
                    in_progress_pages: selectedUrls.length,
                    completed_pages: 0,
                    failed_pages: 0,
                    limit_reached_pages: 0
                }
            ]);
            toast.success('Your website is indexing! Check the websites module for progress.', toastConfig);
            captureEvent('indexing_pages', {
                website: website,
                urls: selectedUrls,
            });
        }).catch((error) => {
            console.error('Error indexing pages:', error);
            toast.error(error.detail, toastConfig);
            setScraping(false);
        });
    };

    const clear = () => {
        setSelectedPages(new Set());
    };

    const filteredPages = pages.filter((page: string) => 
        page.toLowerCase().includes(pageSearchTerm.toLowerCase())
    );

    const selectAllFilteredPages = () => {
        setSelectedPages((prevSelected: Set<string>) => {
            const newSelected = new Set(prevSelected);
            filteredPages.forEach((page: string) => {
                newSelected.add(page);
            });
            return newSelected;
        });
    };

    const deselectAllFilteredPages = () => {
        setSelectedPages((prevSelected: Set<string>) => {
            const newSelected = new Set(prevSelected);
            filteredPages.forEach((page: string) => {
                newSelected.delete(page);
            });
            return newSelected;
        });
    };

    const areAllFilteredPagesSelected = filteredPages.length > 0 && filteredPages.every((page: string) => selectedPages.has(page));
    const areSomeFilteredPagesSelected = filteredPages.some((page: string) => selectedPages.has(page));
    const selectedCount = Array.from(selectedPages as Set<string>).filter((page: string) => pages.includes(page)).length;

    useEffect(() => {
        if (indexedPages && indexedPages.length > 0) {
            setSelectedPages(new Set(indexedPages.map((page: any) => page.url)));
        }
    }, [indexedPages]);

    useEffect(() => {
        if (agentId) {
            get(`/websites/?agent_id=${agentId}`).then((res) => {
                setWebsites(res.data);
                setLoading(false);
            }).catch((error) => {
                console.error('Error fetching websites:', error);
                setLoading(false);
            });
        }
    }, [agentId]);

    useEffect(() => {
        if (!showWebsites && websiteSelected && agentId) {
            get(`/websites/?agent_id=${agentId}`).then((res) => {
                setWebsites(res.data);
                setLoading(false);
                setWebsiteSelected(undefined);
            }).catch((error) => {
                console.error('Error fetching websites:', error);
                setLoading(false);
            });
        }
    }, [showWebsites, websiteSelected]);

    function deleteWebsiteAssociation(websiteId: string) {
        remove(`/agents/${agentId}/websites/${websiteId}`)
            .then((response) => {
                toast.success('Website association deleted', toastConfig);
                setWebsites((prevWebsites) => prevWebsites.filter((website: any) => website.id !== websiteId));
                return response.data;
            })
            .catch((error) => {
                console.error('Error deleting website association:', error);
                toast.error('Error deleting website association', toastConfig);
            });
    }

    const reindexWebsite = (websiteId: string) => {
        setReindexingWebsites(prev => new Set(prev).add(websiteId));
        post('/re-index-pages/', { website_id: websiteId })
            .then((res) => {
                toast.success('Training started!', toastConfig);
                captureEvent('reindexed_pages', {
                    website_id: websiteId,
                    agent_id: agentId,
                });
                setTimeout(() => {
                    get(`/websites/?agent_id=${agentId}`).then((res) => {
                        setWebsites(res.data);
                        setReindexingWebsites(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(websiteId);
                            return newSet;
                        });
                    }).catch((error) => {
                        console.error('Error fetching websites:', error);
                        setReindexingWebsites(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(websiteId);
                            return newSet;
                        });
                    });
                }, 1000);
            })
            .catch((error) => {
                console.error('Error reindexing website:', error);
                toast.error(error.detail || 'Error training pages.', toastConfig);
                setReindexingWebsites(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(websiteId);
                    return newSet;
                });
            });
    };

    const toggleWebsiteExpansion = (websiteId: string) => {
        setExpandedWebsites(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(websiteId)) {
                newExpanded.delete(websiteId);
            } else {
                newExpanded.add(websiteId);
                fetchWebsitePages(websiteId, 1);
            }
            return newExpanded;
        });
    };

    const fetchWebsitePages = async (websiteId: string, pageNumber: number = 1) => {
        try {
            setLoadingPages(prev => new Set(prev).add(websiteId));
            const params = new URLSearchParams({
                website_id: websiteId,
                per_page: '20',
                page: pageNumber.toString()
            });

            const response = await get(`/pages/?${params.toString()}`);
            setWebsitePages(prev => ({
                ...prev,
                [websiteId]: response.data.items
            }));
            setWebsitePageInfo(prev => ({
                ...prev,
                [websiteId]: {
                    currentPage: response.data.page,
                    totalPages: response.data.pages
                }
            }));
        } catch (error) {
            console.error('Error fetching website pages:', error);
            toast.error('Failed to load pages', toastConfig);
        } finally {
            setLoadingPages(prev => {
                const newLoading = new Set(prev);
                newLoading.delete(websiteId);
                return newLoading;
            });
        }
    };

    const handlePageChange = (websiteId: string, newPage: number) => {
        fetchWebsitePages(websiteId, newPage);
        const pagesContainer = document.getElementById(`pages-container-${websiteId}`);
        if (pagesContainer) {
            pagesContainer.scrollTop = 0;
        }
    };

    const fetchFailedPages = async (websiteId: string) => {
        try {
            setLoadingPages(prev => new Set(prev).add(websiteId));
            const params = new URLSearchParams({
                website_id: websiteId,
                index_status: 'Failed',
                per_page: '100'
            });

            const response = await get(`/pages/?${params.toString()}`);
            const failedPages = (response.data.items || []).filter((page: any) => page.index_status === 'Failed');

            setFailedPagesDialog({
                open: true,
                websiteId,
                pages: failedPages
            });

        } catch (error) {
            console.error('Error fetching failed pages:', error);
            toast.error('Failed to load failed pages', toastConfig);
        } finally {
            setLoadingPages(prev => {
                const newLoading = new Set(prev);
                newLoading.delete(websiteId);
                return newLoading;
            });
        }
    };

    const closeFailedPagesDialog = () => {
        setFailedPagesDialog({
            open: false,
            websiteId: '',
            pages: []
        });
    };

    const handleFailedPagesSuccess = () => {
        // Refresh the websites list after successful retraining
        setTimeout(() => {
            if (agentId) {
                get(`/websites/?agent_id=${agentId}`).then((res) => {
                    setWebsites(res.data);
                }).catch((error) => {
                    console.error('Error fetching websites:', error);
                });
            }
        }, 1000);
    };

    const closeModal = () => {
        setSelectedPageId(null);
    };

    const handlePageClick = (pageId: string) => {
        setSelectedPageId(pageId);
    };

    return (
        <div className="flex w-full max-w-full flex-col space-y-8">
            {/* Page Modal */}
            {selectedPageId && <PageModal isOpen={!!selectedPageId} closeModal={closeModal} pageId={selectedPageId} />}

            {/* Add a new website section */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Add website</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Fetch selected pages or crawl an entire site.
                    </p>
                </div>

                <div className="space-y-4 rounded-lg border bg-card p-4">
                        <div className="min-w-0 space-y-1">
                            <h4 className="text-sm font-medium">Website URL</h4>
                            <p className="text-xs text-muted-foreground">Enter your website URL, then choose how to add it to your agent's knowledge.</p>
                        </div>
                    <Input
                        placeholder="https://aimdoc.ai"
                        className="w-full"
                        onChange={(e) => setWebsite(e.target.value)}
                        value={website}
                    />

                    {scraping && (
                        <p className="text-sm text-muted-foreground animate-pulse">
                            If your website has a lot of content, this may take up to 2 minutes
                        </p>
                    )}

                    <div className="grid grid-cols-1 @lg:grid-cols-2 gap-3">
                        {/* Fetch Pages Card */}
                        <div className="rounded-lg border p-4 flex flex-col gap-3">
                            <div className="flex items-start gap-3 flex-1">
                                <SquareMousePointer className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium">Fetch Pages</h4>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Reads your sitemap and retrieves all listed pages, letting you select which ones to train on.
                                    </p>
                                </div>
                            </div>
                            <Button
                                disabled={gettingPages || crawling || !website}
                                variant="outline"
                                className="w-full flex items-center justify-center mt-auto"
                                onClick={() => scrapeWebsite()}
                            >
                                <SquareMousePointer className="w-4 h-4 mr-2" />
                                {gettingPages ? (
                                    <div className="inline-flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-slate-600 dark:text-slate-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Fetching...
                                    </div>
                                ) : (
                                    'Fetch Pages'
                                )}
                            </Button>
                        </div>

                        {/* Crawl Site Card */}
                        <div className="rounded-lg border p-4 flex flex-col gap-3">
                            <div className="flex items-start gap-3 flex-1">
                                <WandSparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium">Crawl Site</h4>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Recommended if you don't have a sitemap. Kicks off a crawling process that discovers and trains on all pages it can find.
                                    </p>
                                </div>
                            </div>
                            {checkLicenseType('standard') ? (
                                <Button
                                    disabled={gettingPages || crawling || !website}
                                    variant="outline"
                                    className="w-full flex items-center justify-center mt-auto"
                                    onClick={() => crawlWebsite()}
                                >
                                    <WandSparkles className="w-4 h-4 mr-2" />
                                    {crawling ? (
                                        <div className="inline-flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-slate-600 dark:text-slate-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Crawling...
                                        </div>
                                    ) : (
                                        'Crawl Site'
                                    )}
                                </Button>
                            ) : (
                                <Button
                                    variant="outline"
                                    className="w-full text-muted-foreground mt-auto"
                                    disabled
                                >
                                    Standard plan required
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Add Existing Site */}
                    <div className="rounded-lg border border-dashed p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0 basis-48">
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium">Add existing site</h4>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Link a website already trained on another agent. Uses global knowledge to share site data across agents.
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => setShowWebsites(true)}
                                className="shrink-0"
                            >
                                Browse sites
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Page selection section */}
            {pages && pages.length > 0 && isOpen && (
                <div className="space-y-3">
                    <div>
                        <h3 className="text-sm font-semibold">Select pages</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Choose the pages that are relevant to this agent.
                        </p>
                    </div>
                    <div className="w-full max-w-full overflow-hidden rounded-lg border bg-card">
                        <div className="border-b p-4">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    placeholder="Search pages..."
                                    value={pageSearchTerm}
                                    onChange={(e) => setPageSearchTerm(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-4 border-b bg-muted/20 px-4 py-3">
                            <label htmlFor="select-all-filtered" className="flex cursor-pointer items-center gap-3 text-sm font-medium">
                                <Checkbox
                                    id="select-all-filtered"
                                    checked={areAllFilteredPagesSelected}
                                    ref={(el: any) => {
                                        if (el) el.indeterminate = areSomeFilteredPagesSelected && !areAllFilteredPagesSelected;
                                    }}
                                    onCheckedChange={(checked) => {
                                        if (checked) {
                                            selectAllFilteredPages();
                                        } else {
                                            deselectAllFilteredPages();
                                        }
                                    }}
                                />
                                Select all shown
                            </label>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>{filteredPages.length} of {pages.length} pages</span>
                                {selectedCount > 0 && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={clear}
                                        className="h-auto px-0 py-0 text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
                                    >
                                        Clear selection
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="max-h-80 divide-y overflow-y-auto">
                            {filteredPages.map((page: string) => (
                                <label
                                    key={page}
                                    htmlFor={page}
                                    className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/40"
                                >
                                    <Checkbox
                                        id={page}
                                        checked={selectedPages.has(page)}
                                        onCheckedChange={() => handleCheckboxChange(page)}
                                        className="mt-0.5 shrink-0"
                                    />
                                    <span className="min-w-0 flex-1 break-all text-sm leading-5">{page}</span>
                                </label>
                            ))}
                            {filteredPages.length === 0 && (
                                <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                                    No pages match your search.
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-4 border-t px-4 py-3">
                            <p className="text-xs text-muted-foreground">
                                {selectedCount} {selectedCount === 1 ? 'page' : 'pages'} selected
                            </p>
                            <Button
                                type="button"
                                onClick={() => indexPages()}
                                disabled={scraping || selectedCount === 0}
                            >
                                {scraping ? (
                                    <div className="inline-flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-slate-600 dark:text-slate-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Training...
                                    </div>
                                ) : (
                                    'Train agent'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Trained websites section */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Trained websites</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Review indexing progress and manage connected sites.
                    </p>
                </div>

                <div className="flex flex-col space-y-2 w-full">
                    {loading ? (
                        <div className="space-y-2">
                            <WebsiteSkeleton />
                        </div>
                    ) : websites.length > 0 ? (
                                websites.map((website: any) => (
                                    <div key={website.id} className="flex flex-col w-full p-3 rounded-md border text-sm">
                                        <div className="w-full">
                                            <div className="flex flex-row justify-between w-full items-center">
                                                <div
                                                    className="flex items-center overflow-hidden cursor-pointer flex-1"
                                                    onClick={() => toggleWebsiteExpansion(website.id)}
                                                >
                                                    {expandedWebsites.has(website.id) ? (
                                                        <ChevronDown className="w-5 h-5 mr-3 text-slate-600 flex-shrink-0 dark:text-slate-300" />
                                                    ) : (
                                                        <ChevronRight className="w-5 h-5 mr-3 text-slate-600 flex-shrink-0 dark:text-slate-300" />
                                                    )}
                                                    <div className="flex items-center overflow-hidden flex-1">
                                                        <div className="text-sm dark:text-slate-200 break-all font-medium overflow-hidden text-ellipsis">
                                                            {website.url}
                                                        </div>
                                                        {website.indexed_at && (
                                                            <div className="flex items-center text-xs text-muted-foreground ml-auto pl-2 whitespace-nowrap">
                                                                <Clock className="w-3 h-3 mr-1" />
                                                                {formatRelativeTime(website.indexed_at)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 ml-4">
                                                    <div className="flex flex-wrap gap-1 items-center justify-end">
                                                        {website.completed_pages > 0 && (
                                                            <span className="text-xs px-2 py-0.5 rounded-md border bg-accent border-border text-green-600 dark:text-green-400">
                                                                {website.completed_pages} Done
                                                            </span>
                                                        )}
                                                        {website.in_progress_pages > 0 && (
                                                            <span className="text-xs px-2 py-0.5 rounded-md border bg-accent border-border text-blue-600 dark:text-blue-400 animate-pulse">
                                                                {website.in_progress_pages} Training
                                                            </span>
                                                        )}
                                                        {website.failed_pages > 0 && (
                                                            <span
                                                                className="text-xs px-2 py-0.5 rounded-md border bg-accent border-border text-red-600 dark:text-red-400 cursor-pointer hover:bg-accent/80"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    fetchFailedPages(website.id);
                                                                }}
                                                            >
                                                                {website.failed_pages} Failed
                                                            </span>
                                                        )}
                                                        {website.limit_reached_pages > 0 && (
                                                            <span className="text-xs px-2 py-0.5 rounded-md border bg-accent border-border text-yellow-600 dark:text-yellow-400">
                                                                {website.limit_reached_pages} Limit
                                                            </span>
                                                        )}
                                                        {!website.completed_pages && !website.in_progress_pages && !website.failed_pages && !website.limit_reached_pages && (
                                                            <span className="text-xs px-2 py-0.5 rounded-md border bg-accent border-border text-slate-600 dark:text-slate-400">
                                                                Pending
                                                            </span>
                                                        )}
                                                    </div>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => reindexWebsite(website.id)}
                                                                    className="flex items-center hover:text-blue-500 flex-shrink-0"
                                                                    disabled={reindexingWebsites.has(website.id)}
                                                                >
                                                                    <RefreshCw className={`w-3 h-3 ${reindexingWebsites.has(website.id) ? 'animate-spin' : ''}`} />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                Re-train this website
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => navigate(`/websites/${website.id}`)}
                                                                    className="flex items-center hover:text-gray-500 flex-shrink-0"
                                                                >
                                                                    <Settings className="w-3 h-3" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                Website settings
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => deleteWebsiteAssociation(website.id)}
                                                        className="flex items-center hover:text-red-500 flex-shrink-0"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            </div>

                                            {website.page_count > 0 && (
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    {website.page_count} total pages indexed
                                                </div>
                                            )}
                                        </div>

                                        {/* Expanded website pages */}
                                        {expandedWebsites.has(website.id) && (
                                            <div className="mt-3 pl-7 border-t pt-2">
                                                <div className="relative">
                                                    {loadingPages.has(website.id) && (
                                                        <div className="absolute inset-0 flex justify-center items-center bg-background/60 z-10">
                                                            <div className="animate-spin h-4 w-4 border-2 border-slate-500 rounded-full border-t-transparent"></div>
                                                        </div>
                                                    )}

                                                    {(websitePages[website.id]?.length > 0) ? (
                                                        <>
                                                            <div
                                                                id={`pages-container-${website.id}`}
                                                                className="space-y-1 max-h-60 overflow-y-auto scrollbar-hidden"
                                                            >
                                                                {websitePages[website.id].map(page => (
                                                                    <div
                                                                        key={page.id}
                                                                        className="flex items-center text-xs py-1 border-b border-border border-dashed hover:bg-accent/50 transition-colors cursor-pointer"
                                                                        onClick={() => handlePageClick(page.id)}
                                                                    >
                                                                        <File className="w-3 h-3 mr-2 text-slate-400" />
                                                                        <div className="flex items-center flex-1 min-w-0 overflow-hidden">
                                                                            <div className="truncate max-w-[350px] lg:max-w-[500px]">{page.url}</div>
                                                                            <div className="flex items-center ml-auto pl-2 flex-shrink-0 space-x-2">
                                                                                {page.tokens && (
                                                                                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                                                                                        {page.tokens.toLocaleString()} chars
                                                                                    </div>
                                                                                )}
                                                                                {page.indexed_at && (
                                                                                    <div className="text-xs text-muted-foreground flex items-center flex-shrink-0">
                                                                                        <Clock className="w-2 h-2 mr-1" />
                                                                                        {formatRelativeTime(page.indexed_at)}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <span className={`ml-2 px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${page.index_status === 'Completed' ? 'text-green-600 dark:text-green-400' :
                                                                                page.index_status === 'In Progress' ? 'text-blue-600 dark:text-blue-400' :
                                                                                    page.index_status === 'Failed' ? 'text-red-600 dark:text-red-400' :
                                                                                        page.index_status === 'Limit Reached' ? 'text-yellow-600 dark:text-yellow-400' :
                                                                                            'text-slate-600 dark:text-slate-400'
                                                                            }`}>
                                                                            {page.index_status === 'Completed' ? 'Done' :
                                                                                page.index_status === 'In Progress' ? 'Training' :
                                                                                    page.index_status === 'Failed' ? 'Failed' :
                                                                                        page.index_status === 'Limit Reached' ? 'Limit' :
                                                                                            'Pending'}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {websitePageInfo[website.id]?.totalPages > 1 && (
                                                                <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                                                                    <div>
                                                                        Page {websitePageInfo[website.id]?.currentPage} of {websitePageInfo[website.id]?.totalPages}
                                                                    </div>
                                                                    <div className="flex space-x-1">
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="h-6 w-6 p-0"
                                                                            onClick={() => handlePageChange(website.id, websitePageInfo[website.id]?.currentPage - 1)}
                                                                            disabled={websitePageInfo[website.id]?.currentPage <= 1 || loadingPages.has(website.id)}
                                                                        >
                                                                            <ChevronRight className="h-3 w-3 rotate-180" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="h-6 w-6 p-0"
                                                                            onClick={() => handlePageChange(website.id, websitePageInfo[website.id]?.currentPage + 1)}
                                                                            disabled={websitePageInfo[website.id]?.currentPage >= websitePageInfo[website.id]?.totalPages || loadingPages.has(website.id)}
                                                                        >
                                                                            <ChevronRight className="h-3 w-3" />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : !loadingPages.has(website.id) ? (
                                                        <div className="text-xs text-center text-muted-foreground py-6">
                                                            No pages found
                                                        </div>
                                                    ) : (
                                                        <div className="py-6">&nbsp;</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="rounded-lg border border-dashed p-8 text-center">
                                    <div className="flex flex-col items-center gap-2">
                                        <p className="text-sm font-medium text-muted-foreground">No trained websites</p>
                                        <p className="text-xs text-muted-foreground">
                                            Add a website above to train your agent on its content.
                                        </p>
                                    </div>
                                </div>
                    )}
                </div>
            </div>

            {showWebsites && (
                <WebsitesList
                    agentId={agentId}
                    setShowWebsites={setShowWebsites}
                    setWebsitesSelected={setWebsiteSelected}
                />
            )}

            {/* Failed Pages Dialog */}
            <FailedPagesDialog
                open={failedPagesDialog.open}
                websiteId={failedPagesDialog.websiteId}
                pages={failedPagesDialog.pages}
                onClose={closeFailedPagesDialog}
                onSuccess={handleFailedPagesSuccess}
            />
        </div>
    )
}
